import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Download, Search, UserX } from 'lucide-react'
import { useMemo, useState, useEffect, type ReactElement } from 'react'
import { z } from 'zod'

import { DataTablePagination } from '~/components/data/DataTablePagination'
import { MobileFilterDisclosure } from '~/components/data/MobileFilterDisclosure'
import {
  LockUserDialog,
  ResetPasswordDialog,
  usersKeys,
  useUnlockUserMutation,
  useVerifyEmailMutation,
  type UserRow,
  type UserStatus,
  type UsersListResponse,
} from '~/features/users'
import { UsersTable } from '~/features/users'
import type { UserRowAction } from '~/features/users/components/UsersTable'
import { normalizeUserRow } from '~/features/users/queries'
import { http } from '~/lib/http'
import { useDebouncedValue } from '~/lib/query/use-debounced-value'
import {
  toPaginatedResult,
  usePaginatedQuery,
  type PaginatedParams,
  type PaginatedResult,
} from '~/lib/query/use-paginated-query'
import { useUrlState } from '~/lib/query/use-url-state'

type StatusFilter = 'all' | 'verified' | 'unverified' | 'locked'

const usersSearchSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(10).max(100).default(25),
  search: z.string().optional(),
  status: z.enum(['all', 'verified', 'unverified', 'locked']).default('all'),
})
type UsersSearch = z.infer<typeof usersSearchSchema>

const URL_DEFAULTS: UsersSearch = {
  page: 1,
  limit: 25,
  status: 'all',
}

/**
 * `/admin/users` — User Management list (URL-paginated).
 *
 * URL search keys: `page`, `limit`, `search`, `status`.
 *
 * Spec: 08-users.md §D. The route fetcher accepts either the new
 * standardized envelope shape (`{ items, pagination: { ... } }`) or
 * the legacy `{ users, total }` shape, so the page works against
 * Workers that haven't migrated yet (per Part 2 server contract).
 */
export const Route = createFileRoute('/admin/users/')({
  validateSearch: usersSearchSchema,
  component: UsersPage,
})

interface UsersListPayload {
  readonly users?: readonly unknown[]
  readonly items?: readonly unknown[]
  readonly total?: number
  readonly pagination?: {
    readonly page?: number
    readonly limit?: number
    readonly total?: number
    readonly totalPages?: number
  }
}

async function fetchUsersPage(
  params: PaginatedParams,
): Promise<PaginatedResult<UserRow>> {
  const status = params.filters?.status as StatusFilter | undefined
  const VERIFIED_FILTERS = new Set(['verified', 'unverified'])
  // Verified / unverified must be client-filtered because the legacy
  // Worker maps `status` to `account_status` (active/locked/suspended)
  // and ignores email-verified booleans. Mirrors features/users/queries.
  const clientFilterEmailVerified =
    status === 'verified'
      ? true
      : status === 'unverified'
        ? false
        : null

  const query: Record<string, string | number | undefined> = {
    page: params.page,
    limit:
      clientFilterEmailVerified === null
        ? params.limit
        : params.limit * 4,
    offset: (params.page - 1) * params.limit,
  }
  if (params.search) query.search = params.search
  if (params.sort) query.sort = params.sort
  if (status && status !== 'all' && !VERIFIED_FILTERS.has(status)) {
    query.status = status
  }

  const raw = await http.get<UsersListPayload>('/api/v1/admin/users', {
    query,
  })
  // The legacy worker returns snake_case rows (avatar_url, email_verified,
  // account_status, …) — normalize to the camelCase contract the UI reads.
  const rawRows = (raw.users ?? raw.items ?? []) as readonly unknown[]
  let rows = rawRows.map(normalizeUserRow) as readonly UserRow[]
  let total = raw.pagination?.total ?? raw.total ?? rows.length
  if (clientFilterEmailVerified !== null) {
    rows = rows.filter((u) => u.emailVerified === clientFilterEmailVerified)
    total = rows.length
  }
  return toPaginatedResult(rows, total, params, raw.pagination)
}

function UsersPage(): ReactElement {
  const navigate = useNavigate()
  const search = Route.useSearch()
  const [, setUrl] = useUrlState(usersSearchSchema, URL_DEFAULTS)

  // Local search input — debounced before being pushed to URL state.
  const [searchInput, setSearchInput] = useState<string>(search.search ?? '')
  const debouncedSearch = useDebouncedValue(searchInput, 300)
  useEffect(() => {
    const normalized = debouncedSearch.trim() === '' ? undefined : debouncedSearch.trim()
    if (normalized !== search.search) {
      setUrl({ search: normalized, page: 1 })
    }
  }, [debouncedSearch, search.search, setUrl])

  const filters = useMemo<Record<string, string | undefined>>(
    () => ({ status: search.status }),
    [search.status],
  )
  const params: PaginatedParams = {
    page: search.page,
    limit: search.limit,
    search: search.search,
    filters,
  }

  const usersQuery = usePaginatedQuery<UserRow>({
    queryKey: [...usersKeys.all, 'list-paginated'],
    params,
    fetcher: fetchUsersPage,
  })

  const users = useMemo(
    () => usersQuery.data?.data ?? [],
    [usersQuery.data],
  )
  const pagination = usersQuery.data?.pagination
  // For the header counts we want totals derived from the visible page —
  // the API doesn't return a totals-by-status aggregate yet, so we keep
  // the previous behavior of bucketing the current page.
  const counts = useMemo(() => bucketCounts(users), [users])

  // Active row-action dialog. Locks and password resets have dedicated
  // dialogs; the lighter actions (verify-email and unlock) use the small
  // inline confirm at the bottom of this route. The
  // mutations themselves invalidate `usersKeys.all` so the list refetches
  // automatically on success.
  const [actionTarget, setActionTarget] = useState<{
    readonly action: Exclude<UserRowAction, 'view' | 'lock' | 'reset-password'>
    readonly user: UserRow
  } | null>(null)
  const [lockTarget, setLockTarget] = useState<UserRow | null>(null)
  const [resetTarget, setResetTarget] = useState<UserRow | null>(null)
  const unlockMutation = useUnlockUserMutation({
    onSuccess: () => setActionTarget(null),
  })
  const verifyMutation = useVerifyEmailMutation({
    onSuccess: () => setActionTarget(null),
  })

  function handleRowAction(action: UserRowAction, user: UserRow): void {
    if (action === 'view') {
      void navigate({
        to: '/admin/users/$userId',
        params: { userId: user.id },
      })
      return
    }
    if (action === 'lock') {
      setLockTarget(user)
      return
    }
    if (action === 'reset-password') {
      setResetTarget(user)
      return
    }
    setActionTarget({ action, user })
  }

  function confirmActionTarget(): void {
    if (actionTarget === null) return
    const { action, user } = actionTarget
    if (action === 'unlock') {
      unlockMutation.mutate({ userId: user.id })
    } else if (action === 'verify-email') {
      verifyMutation.mutate({ userId: user.id })
    }
  }

  const inlinePending =
    unlockMutation.isPending ||
    verifyMutation.isPending
  const inlineError =
    unlockMutation.error?.message ??
    verifyMutation.error?.message ?? null

  function exportVisibleUsers(): void {
    const header = ['Name', 'Email', 'Status', 'Email verified', 'Created at', 'Last sign-in']
    const quote = (value: string | null | undefined): string =>
      `"${(value ?? '').replaceAll('"', '""')}"`
    const rows = users.map((user) => [
      user.displayName,
      user.email,
      user.status,
      user.emailVerified ? 'yes' : 'no',
      user.createdAt,
      user.lastSignInAt,
    ].map(quote).join(','))
    const blob = new Blob([[header.join(','), ...rows].join('\n')], {
      type: 'text/csv;charset=utf-8',
    })
    const href = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = href
    anchor.download = `kairos-users-${new Date().toISOString().slice(0, 10)}.csv`
    anchor.click()
    URL.revokeObjectURL(href)
  }

  function closeInline(): void {
    if (inlinePending) return
    setActionTarget(null)
    unlockMutation.reset()
    verifyMutation.reset()
  }

  return (
    <div className="ka-page">
      <header className="ka-page-header">
        <div>
          <h1 className="ka-page-header-title">User Management</h1>
          <div className="ka-page-header-subtitle">
            {subtitleFor(search.status, pagination?.total ?? users.length, counts)}
          </div>
        </div>
        <div className="ka-page-header-actions">
          <Link
            to="/admin/users/unverified"
            className="ka-btn ka-btn--ghost ka-btn--sm"
          >
            <UserX size={12} aria-hidden="true" /> Unverified queue
          </Link>
          <button
            type="button"
            className="ka-btn ka-btn--secondary ka-btn--sm"
            onClick={exportVisibleUsers}
            disabled={users.length === 0}
          >
            <Download size={12} aria-hidden="true" /> Export CSV
          </button>
        </div>
      </header>

      <div className="ka-filter-strip" role="search" aria-label="User filters">
        <MobileFilterDisclosure
          id="user-list-filters"
          activeCount={search.status === 'all' ? 0 : 1}
        >
          <div className="ka-filter-group">
            <span className="ka-filter-group-label">Status</span>
            {(['all', 'verified', 'unverified', 'locked'] as const).map(
              (option) => (
                <button
                  key={option}
                  type="button"
                  className={`ka-detail-chip${search.status === option ? ' is-active' : ''}`}
                  aria-pressed={search.status === option}
                  onClick={() => setUrl({ status: option, page: 1 })}
                >
                  {labelForStatus(option)}
                </button>
              ),
            )}
          </div>
        </MobileFilterDisclosure>
        <div className="ka-filter-search">
          <Search size={14} aria-hidden="true" />
          <input
            type="search"
            className="ka-field-input"
            placeholder="Search name · email · ID…"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            aria-label="Search users"
          />
        </div>
      </div>

      {usersQuery.isError ? (
        <div className="ka-card" role="alert">
          <div className="ka-empty">
            <div className="ka-empty-title">Couldn't load users</div>
            <div className="ka-empty-body">
              {isForbidden(usersQuery.error)
                ? "Restricted — you don't have access to user management. Request access from a super_admin."
                : usersQuery.error instanceof Error
                  ? usersQuery.error.message
                  : 'Unknown error'}
            </div>
          </div>
        </div>
      ) : (
        <>
          <UsersTable
            users={users}
            isLoading={usersQuery.isPending}
            onOpenUser={(id) =>
              void navigate({
                to: '/admin/users/$userId',
                params: { userId: id },
              })
            }
            onRowAction={handleRowAction}
          />
          <DataTablePagination
            page={pagination?.page ?? search.page}
            limit={pagination?.limit ?? search.limit}
            total={pagination?.total ?? users.length}
            isLoading={usersQuery.isPending}
            onChange={({ page, limit }) => setUrl({ page, limit })}
          />
        </>
      )}

      {lockTarget !== null ? (
        <LockUserDialog
          userId={lockTarget.id}
          userEmail={lockTarget.email}
          open
          onClose={() => setLockTarget(null)}
        />
      ) : null}

      {resetTarget !== null ? (
        <ResetPasswordDialog
          userId={resetTarget.id}
          userEmail={resetTarget.email}
          open
          onClose={() => setResetTarget(null)}
        />
      ) : null}

      {actionTarget !== null ? (
        <InlineConfirm
          title={inlineTitleFor(actionTarget.action, actionTarget.user.email)}
          body={inlineBodyFor(actionTarget.action)}
          confirmLabel={inlineConfirmLabelFor(actionTarget.action)}
          destructive={false}
          isSubmitting={inlinePending}
          errorMessage={inlineError ?? undefined}
          onClose={closeInline}
          onConfirm={confirmActionTarget}
        />
      ) : null}
    </div>
  )
}

function inlineTitleFor(
  action: Exclude<UserRowAction, 'view' | 'lock' | 'reset-password'>,
  email: string,
): string {
  if (action === 'unlock') return `Unlock ${email}?`
  if (action === 'verify-email') return `Mark ${email} as verified?`
  return `Update ${email}?`
}

function inlineBodyFor(
  action: Exclude<UserRowAction, 'view' | 'lock' | 'reset-password'>,
): string {
  if (action === 'unlock') {
    return 'Unlocking restores sign-in access immediately. The audit log keeps the original lock reason.'
  }
  if (action === 'verify-email') {
    return 'Marks the email confirmed without requiring the user to click the verification link.'
  }
  return 'This administrative action is recorded in the audit log.'
}

function inlineConfirmLabelFor(
  action: Exclude<UserRowAction, 'view' | 'lock' | 'reset-password'>,
): string {
  if (action === 'unlock') return 'Unlock user'
  if (action === 'verify-email') return 'Mark verified'
  return 'Confirm'
}

interface InlineConfirmProps {
  readonly title: string
  readonly body: string
  readonly confirmLabel: string
  readonly destructive?: boolean
  readonly isSubmitting?: boolean
  readonly errorMessage?: string | undefined
  readonly onClose: () => void
  readonly onConfirm: () => void
}

/**
 * Lightweight modal for low-risk row actions that don't need a typed
 * confirm. Co-located with the route to avoid a separate dialog file
 * per verb — the heavyweight `LockUserDialog` lives in `features/users`.
 */
function InlineConfirm({
  title,
  body,
  confirmLabel,
  destructive = false,
  isSubmitting = false,
  errorMessage,
  onClose,
  onConfirm,
}: InlineConfirmProps): ReactElement {
  return (
    <div
      role="presentation"
      className="ka-modal-backdrop ka-modal-backdrop--blur"
      onClick={(event) => {
        if (event.target === event.currentTarget && !isSubmitting) onClose()
      }}
    >
      <div role="dialog" aria-modal="true" className="ka-modal">
        <header className="ka-modal-head ka-modal-head--padded">
          <div className="ka-modal-title-wrap">
            <h2 className="ka-modal-head-title">{title}</h2>
          </div>
        </header>
        <div className="ka-modal-body ka-modal-body--plain">
          <p className="ka-modal-desc">{body}</p>
          {errorMessage !== undefined ? (
            <div role="alert" className="ka-error-banner">
              {errorMessage}
            </div>
          ) : null}
        </div>
        <footer className="ka-modal-foot ka-modal-foot--tinted">
          <button
            type="button"
            className="ka-btn ka-btn--ghost"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className={destructive ? 'ka-btn ka-btn--destructive' : 'ka-btn ka-btn--primary'}
            onClick={onConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Working…' : confirmLabel}
          </button>
        </footer>
      </div>
    </div>
  )
}

function labelForStatus(status: StatusFilter): string {
  if (status === 'all') return 'All'
  if (status === 'verified') return 'Verified'
  if (status === 'unverified') return 'Unverified'
  return 'Locked'
}

function subtitleFor(
  status: StatusFilter,
  total: number,
  counts: BucketCounts,
): string {
  // When a filter is active the per-status buckets reflect only the
  // visible page and are misleading ("0 unverified" while filtered TO
  // unverified). Show a single, filter-aware sentence instead.
  if (status === 'verified') return `${total} verified`
  if (status === 'unverified') return `${total} unverified`
  if (status === 'locked') return `${total} locked`
  return `${total} total · ${counts.unverified} unverified · ${counts.locked} locked · ${counts.suspended} suspended`
}

interface BucketCounts {
  readonly unverified: number
  readonly locked: number
  readonly suspended: number
}

function bucketCounts(users: ReadonlyArray<UserRow>): BucketCounts {
  return users.reduce<BucketCounts>(
    (acc, user) => bumpStatus(acc, user.status),
    { unverified: 0, locked: 0, suspended: 0 },
  )
}

function bumpStatus(counts: BucketCounts, status: UserStatus): BucketCounts {
  if (status === 'unverified') {
    return { ...counts, unverified: counts.unverified + 1 }
  }
  if (status === 'locked') {
    return { ...counts, locked: counts.locked + 1 }
  }
  if (status === 'suspended') {
    return { ...counts, suspended: counts.suspended + 1 }
  }
  return counts
}

function isForbidden(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false
  const candidate = error as { readonly code?: unknown }
  return candidate.code === 'forbidden'
}

// Re-exported for completeness — referenced by the unused-import lint rule.
export type { UsersListResponse }
